import { useTranslation } from "react-i18next";
import { Alert, Box, Button, CircularProgress, Grid, MenuItem, Stack, styled } from "@mui/material";
import { Link, useParams, useSearchParams } from "react-router";
import BlockIcon from '@mui/icons-material/Block'
import AddIcon from '@mui/icons-material/Add';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import SyncIcon from '@mui/icons-material/Sync'
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled'
import SyncProblemRoundedIcon from '@mui/icons-material/SyncProblemRounded'
import PublishProductsToStoreDialog from "../AddProductListing/PublishProductsToStoreDialog";
import { useAppSelector } from "../../../Common/_hooks/useAppSelector";
import useStoreFlagError from "../../../Common/_hooks/Store/useStoreFlagError";
import ItemList, { type ItemListFetchOptions } from "../../../Common/ItemList/ItemList";
import { type ChangeEvent, type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { StoreProductStore, StoreProductSyncState } from "../../../UserData/_stores/Store/StoreProductStore";
import ProductSkeletonList from "../../Products/ProductSkeletonList";
import { useAppDispatch } from "../../../Common/_hooks/useAppDispatch";
import { EditStoreProductListLoader, InvalidateStoreProducts } from "../../../UserData/_actions/Store/StoreProductStoreActions";
import StoreProduct from "./StoreProduct";
import { FetchStoreProducts } from "../../../UserData/_actions/Store/StoreProductActions";
import StoreProductListActions from "./StoreProductListActions";
import { FetchStoreData, RefreshStoreProducts } from "../../../UserData/_actions/Store/StoreActions";
import { SaveUserPrefs } from "../../../UserData/_actions/UserDataActions";
import SelectV2 from "../../../Common/Form/SelectV2";
import useCheckStoreFeatures from "../../../Common/_hooks/Store/useCheckStoreFeatures";
import type { ProductSortType } from "../../../UserData/_stores/UserPrefsStore";
import StoreProductSearchBar, { type StoreProductSearchParams as BaseStoreProductSearchParams } from "./StoreProductSearchBar";
import { ClearStoreSelectedProduct, SetStoreSelectedProducts } from "../../../UIData/_actions/Account/AccountStoreActions";
import useShiftRangeSelection from "../../../Common/_hooks/useShiftRangeSelection";
import StoreHeader from "../StoreHeader";
import ProductPublishNotice, { isStorePublishingProducts } from "./ProductPublishNotice";
import AccountFilterDrawer from "../../Filters/AccountFilterDrawer";
import AccountFilterSection from "../../Filters/AccountFilterSection";
import AccountLimitFilter from "../../Filters/AccountLimitFilter";
import AccountLayoutFilter from "../../Filters/AccountLayoutFilter";
import AccountNoResults from "../../AccountNoResults";
import AccountSortFilter, { type AccountSortOption } from "../../Filters/AccountSortFilter";

const SEARCH_FIELDS = ['search', 'productTypes', 'state', 'sort']
const SYNC_STATES: Array<'all' | StoreProductSyncState> = ['all', 'synced', 'not-synced', 'partially', 'ignored']
const SORT_OPTIONS: ProductSortType[] = ['n-to-o', 'o-to-n', 'a-to-z', 'z-to-a']

const SyncStatusOption = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  gap: theme.spacing(0.75),
  maxWidth: '100%',
  minWidth: 0,

  '& .MuiSvgIcon-root': {
    fontSize: 15,
    flexShrink: 0,
  },
}))

type StoreProductSearchParams = BaseStoreProductSearchParams & {
  state: StoreProductSyncState[]
  sort: ProductSortType[]
}

export default function StoreProductList() {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('account-v2')
  const [tR] = useTranslation('routes')

  const params = useParams<{ storeSlug: string }>()
  const [searchParams] = useSearchParams()
  const listId = useMemo(() => `store-products-${params.storeSlug ?? 'default'}`, [params.storeSlug])

  const store = useAppSelector(state => state.get('userData').get('stores').find(store => store.get('slug') === params.storeSlug))
  const userLayout = useAppSelector(state => state.get('userData').get('prefs').get('accountProductsLayout'))
  const userSort = useAppSelector(state => state.get('userData').get('prefs').get('accountProductsSort'))
  const userLimit = useAppSelector(state => state.get('userData').get('prefs').get('accountPageLimit'))
  const refreshLoader = useAppSelector(state => state.get('UIData').get('loaders').get('refresh_store_products'))
  const storeProductSelectionClearCounter = useAppSelector(state => state.get('UIData').get('account').get('storeProductSelectionClearCounter'))
  const { canCreateProduct } = useCheckStoreFeatures(params.storeSlug ?? '')

  // Init search bar with url search param
  const searchQuery = searchParams.get('search')
  const productTypesParam = searchParams.get('productTypes')
  const syncState = searchParams.get('state')
  const sortParam = searchParams.get('sort')
  const sort = sortParam
    ? sortParam.split(',').filter((value): value is ProductSortType => SORT_OPTIONS.includes(value as ProductSortType))
    : []
  const [search, setSearch] = useState<StoreProductSearchParams>({
    productTypes: productTypesParam ? productTypesParam.split(',').filter(Boolean) : [],
    search: searchQuery ? searchQuery.split(',').filter(Boolean) : [],
    state: syncState ? syncState.split(',').filter((value): value is StoreProductSyncState => value !== 'all' && SYNC_STATES.includes(value as 'all' | StoreProductSyncState)) : [],
    sort: sort.length > 0 ? sort : [userSort],
  })
  const defaultSearch = useMemo<StoreProductSearchParams>(() => ({
    productTypes: [],
    search: [],
    state: [],
    sort: [userSort],
  }), [userSort])

  const flagError = useStoreFlagError(store?.get('slug') ?? '')
  const storeIntegration = useAppSelector(state => store?.get('slug')
    ? state.get('appData').get('storeIntegrations').get(store.get('slug'))
    : undefined
  )
  const isMissingScope = !!store?.get('is_missing_scope')
  const isLegacyIntegration = !!storeIntegration?.get('legacy')
  const hasSetupPage = !!storeIntegration && !isLegacyIntegration
  const selectionFilterKey = `${params.storeSlug ?? ''}|${search.search.join(',')}|${search.productTypes.join(',')}|${search.state.join(',')}`
  const visibleStoreProductItemIds = store
    ?.get('products')
    .get('itemLists')
    .get(listId)
    ?.get('items')
  const visibleStoreProductIds = useMemo(() => (
    visibleStoreProductItemIds
      ?.valueSeq()
      .map(id => Number(id))
      .filter((id): id is number => Number.isFinite(id))
      .toArray() ?? []
  ), [visibleStoreProductItemIds])
  const { getSelectionIds, resetSelectionRange } = useShiftRangeSelection(visibleStoreProductIds)

  const [limit, setLimit] = useState(userLimit)
  const [layout, setLayout] = useState(userLayout)
  const [publishProductsToStoreDialogOpened, setPublishProductsToStoreDialogOpened] = useState(false)
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const previousStoreSlug = useRef<string | undefined>(params.storeSlug)
  const previousSyncingProducts = useRef(false)
  const previousAddingProducts = useRef(false)
  const storeProductTypes = useAppSelector(state => state
    .get('userData')
    .get('stores')
    .find(store => store.get('slug') === params.storeSlug)
    ?.get('productTypes')
  )
  const productTypeOptions = useMemo(() => {
    if (!storeProductTypes) return [];

    return storeProductTypes.entrySeq().map(([id, name]) => ({
      id,
      label: name,
    })).toArray()
  }, [storeProductTypes])

  const fetchItems = useCallback((listId: string, formData: FormData, options: ItemListFetchOptions = {}) => {
    if (!params.storeSlug) return;

    if (!options.silent) {
      dispatch(EditStoreProductListLoader(params.storeSlug, listId, true));
    }

    dispatch(FetchStoreProducts(params.storeSlug, listId, formData).set({
      onSuccess: () => {
        if (!params.storeSlug) return;

        dispatch(EditStoreProductListLoader(params.storeSlug, listId, false));
      },
      onFailure: () => {
        if (!params.storeSlug) return;

        dispatch(EditStoreProductListLoader(params.storeSlug, listId, false));
      }
    }));
  }, [dispatch, params.storeSlug])

  const onSelectStoreProduct = useCallback((id: number, shiftKey = false) => {
    dispatch(SetStoreSelectedProducts(getSelectionIds(id, shiftKey), true))
  }, [dispatch, getSelectionIds])

  const onUnselectStoreProduct = useCallback((id: number, shiftKey = false) => {
    dispatch(SetStoreSelectedProducts(getSelectionIds(id, shiftKey), false))
  }, [dispatch, getSelectionIds])

  const renderItem = useCallback((item: StoreProductStore) => {
    return <StoreProduct
      key={item.get('id')}
      item={item}
      layout={layout}
      onSelect={onSelectStoreProduct}
      onUnselect={onUnselectStoreProduct}
    />
  }, [layout, onSelectStoreProduct, onUnselectStoreProduct])

  const renderList = useCallback((items: ReactElement | null, links: ReactElement | null, loading: boolean) => {
    return <Box
      component="div"
      sx={{
        pt: layout === 'grid' ? 0 : {
          xs: 2,
          md: 3,
        }
      }}
    >
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {loading
          ? <ProductSkeletonList count={limit} layout={layout} />
          : (items ?? <Grid item xs={12}>
            <AccountNoResults />
          </Grid>
          )
        }
      </Grid>

      {links}
    </Box>
  }, [t, limit, layout])

  const onRefreshProducts = useCallback(() => {
    if (!store) return;

    dispatch(RefreshStoreProducts(store.get('slug')))
  }, [dispatch, store])

  const onPublishProductsToStoreDialogOpen = useCallback(() => {
    setPublishProductsToStoreDialogOpened(true)
  }, [])

  const onPublishProductsToStoreDialogClose = useCallback(() => {
    setPublishProductsToStoreDialogOpened(false)
  }, [])

  const onChangeSyncState = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const nextState = e.target.value

    setSearch(value => ({
      ...value,
      state: nextState === 'all' ? [] : [nextState as StoreProductSyncState],
    }))
  }, [])

  const onChangeSort = useCallback((nextSort: ProductSortType) => {
    setSearch(value => ({
      ...value,
      sort: [nextSort],
    }))

    if (nextSort === userSort) {
      return
    }

    const fd = new FormData()
    fd.append('accountProductsSort', nextSort)
    dispatch(SaveUserPrefs(fd, true))
  }, [dispatch, userSort])

  useEffect(() => {
    if (sortParam !== null) {
      return
    }

    setSearch(value => {
      if (value.sort[0] === userSort) {
        return value
      }

      return {
        ...value,
        sort: [userSort],
      }
    })
  }, [sortParam, userSort])

  useEffect(() => {
    resetSelectionRange()
    dispatch(ClearStoreSelectedProduct())
  }, [dispatch, resetSelectionRange, selectionFilterKey])

  useEffect(() => {
    resetSelectionRange()
  }, [resetSelectionRange, storeProductSelectionClearCounter])

  useEffect(() => {
    return () => {
      dispatch(ClearStoreSelectedProduct())
    }
  }, [dispatch])

  const onSearchChange = useCallback((update: (value: BaseStoreProductSearchParams) => BaseStoreProductSearchParams) => {
    setSearch(value => update(value) as StoreProductSearchParams)
  }, [])

  const renderSyncState = useCallback((value: StoreProductSyncState) => {
    const label = value === 'synced'
      ? t('Synced')
      : (value === 'partially'
        ? t('Partially synced')
        : (value === 'ignored' ? t('Ignored') : t('Not synced')))
    const icon = value === 'synced'
      ? <SyncIcon fontSize="inherit" />
      : (value === 'partially'
        ? <SyncProblemRoundedIcon fontSize="inherit" />
        : (value === 'ignored' ? <BlockIcon fontSize="inherit" /> : <SyncDisabledIcon fontSize="inherit" />))
    const color = value === 'not-synced'
      ? 'text.secondary'
      : (value === 'synced'
        ? 'success.main'
        : (value === 'partially' ? 'info.main' : 'warning.main'))

    return <MenuItem key={value} value={value}>
      <SyncStatusOption
        sx={{
          color,
        }}
      >
        {icon}
        {label}
      </SyncStatusOption>
    </MenuItem>
  }, [t])

  const isSyncingProducts = !!store?.get('is_syncing_products')
  const isAddingProducts = isStorePublishingProducts(store)

  useEffect(() => {
    if (refreshTimer.current !== null) {
      clearInterval(refreshTimer.current)
      refreshTimer.current = null
    }

    if (!isSyncingProducts && !isAddingProducts) {
      return
    }

    refreshTimer.current = setInterval(() => {
      if (params.storeSlug) {
        dispatch(FetchStoreData(params.storeSlug))
      }
    }, 5000)

    return () => {
      if (refreshTimer.current !== null) {
        clearInterval(refreshTimer.current)
        refreshTimer.current = null
      }
    }
  }, [dispatch, isSyncingProducts, isAddingProducts, params.storeSlug])

  useEffect(() => {
    if (previousStoreSlug.current !== params.storeSlug) {
      previousStoreSlug.current = params.storeSlug
      previousSyncingProducts.current = isSyncingProducts
      previousAddingProducts.current = isAddingProducts
      return
    }

    const shouldInvalidate = (
      (previousSyncingProducts.current && !isSyncingProducts)
      || (previousAddingProducts.current && !isAddingProducts)
    );

    if (shouldInvalidate && params.storeSlug) {
      dispatch(InvalidateStoreProducts(params.storeSlug))
    }

    previousSyncingProducts.current = isSyncingProducts
    previousAddingProducts.current = isAddingProducts
  }, [dispatch, isAddingProducts, isSyncingProducts, params.storeSlug])

  if (!store) return null;

  const selectedSyncState = search.state[0] ?? 'all'
  const selectedSort = search.sort[0] ?? userSort
  const sortOptions: AccountSortOption[] = [
    { value: 'n-to-o', label: t('Newest first') },
    { value: 'o-to-n', label: t('Oldest first') },
    { value: 'a-to-z', label: t('A to Z') },
    { value: 'z-to-a', label: t('Z to A') },
  ]

  return <div>
    <StoreHeader
      title={t('Products')}
      actions={<Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<RefreshRoundedIcon />}
          disabled={!!store.get('is_syncing_products') || !!refreshLoader}
          onClick={onRefreshProducts}
        >{t('Refresh products')}</Button>
        {canCreateProduct ? <Button
          color="accent"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onPublishProductsToStoreDialogOpen}
        >{t('Publish product')}</Button> : null}
      </Stack>}
    />

    {canCreateProduct ? <PublishProductsToStoreDialog
      opened={publishProductsToStoreDialogOpened}
      store={store.get('slug')}
      onClose={onPublishProductsToStoreDialogClose}
    /> : null}

    <Stack spacing={2}>
      {flagError ? <Alert
        severity="error"
        variant="outlined"
        action={hasSetupPage ? <Button
          color="inherit"
          size="small"
          component={Link}
          to={tR('/account/stores/connect/{{store}}', { store: store.get('slug') })}
        >{t('Reconnect')}</Button> : undefined}
      >{flagError}</Alert> : null}

      {isLegacyIntegration && store.get('flagged') ? <Alert
        severity="warning"
        variant="outlined"
      >{t('This {{store}} connection is part of a legacy integration. Existing stores can often keep working, but if this connection fails you will not be able to reconnect it from the new account UI.', { store: store.get('name') })}</Alert> : null}

      {isMissingScope ? <Alert
        severity="warning"
        variant="outlined"
        action={hasSetupPage ? <Button
          color="inherit"
          size="small"
          component={Link}
          to={tR('/account/stores/connect/{{store}}', { store: store.get('slug') })}
        >{t('Reconnect')}</Button> : undefined}
      >{hasSetupPage
        ? t('In order to enable some features of the integration, we need you to reconnect your store.')
        : t('Some integration features require reconnecting your store, but reconnecting {{store}} is no longer supported in the new account UI.', { store: store.get('name') })}</Alert> : null}

      <Box component="div" sx={{ flexGrow: 1, minWidth: 0 }}>
        <StoreProductSearchBar
          placeholder={t('Search your products...')}
          value={search}
          onChange={onSearchChange}
          filterOptions={productTypeOptions}
        >
          <AccountFilterDrawer activeCount={selectedSyncState === 'all' ? 0 : 1}>
            <AccountFilterSection title={t('Filters')}>
              <SelectV2
                label={t('Sync status')}
                value={selectedSyncState}
                onChange={onChangeSyncState}
              >
                <MenuItem value="all">{t('All sync states')}</MenuItem>
                {renderSyncState('synced')}
                {renderSyncState('not-synced')}
                {renderSyncState('partially')}
                {renderSyncState('ignored')}
              </SelectV2>
            </AccountFilterSection>

            <AccountFilterSection title={t('Display')}>
              <AccountSortFilter
                label={t('Sort by')}
                value={selectedSort}
                options={sortOptions}
                onChange={onChangeSort}
              />
              <AccountLimitFilter
                label={t('Results per page')}
                value={limit}
                onChange={setLimit}
              />
              <AccountLayoutFilter
                label={t('Layout')}
                value={layout}
                onChange={setLayout}
              />
            </AccountFilterSection>
          </AccountFilterDrawer>
        </StoreProductSearchBar>
      </Box>
    </Stack>

    {store.get('is_syncing_products') ? <Alert
      severity="info"
      variant="outlined"
      icon={<CircularProgress color="inherit" size={20} />}
      sx={{
        mb: 2,
      }}
    >{t('Refreshing product data...')}</Alert> : null}

    <ProductPublishNotice store={store} />

    <StoreProductListActions
      listId={listId}
      search={search}
    />

    <ItemList
      listId={listId}
      dataType="storeProducts"
      paginationStore={store.get('products')}
      fetchItems={fetchItems}
      limit={limit}
      searchQuery={search}
      defaultSearchQuery={defaultSearch}
      searchFields={SEARCH_FIELDS}
      item={renderItem}
      render={renderList}
    />
  </div>
}
